import { AvailableCouponLabel, EnhancedProductCard, PRODUCT_CATEGORY_TYPE, PricePowerLabel, PricePowerLabelType, ProductAggregationTag, ProductCard, ProductInfoLabel, PromotionLabelType, ScpSourceEnum } from "@/types/productCard";
import { EnhancedPriceLabel } from "@/types/productCard";
import { getProductJumpConfig } from "./getProductJumpLink";
import { Level, reportError } from "../owl";

// scpSource -> ProductCategoryType 的映射
export const PRODUCT_CATEGORY_TYPE_MAP = {
  [ScpSourceEnum.wmCoupon]: PRODUCT_CATEGORY_TYPE?.WAIMAI,
  [ScpSourceEnum.phf]: PRODUCT_CATEGORY_TYPE?.WAIMAI,
} as Record<ScpSourceEnum, PRODUCT_CATEGORY_TYPE>;

// 商品卡片的供给标签的图片
export const SUPPLY_TAG_IMAGE_MAP = {
  // 神抢手
  [ScpSourceEnum.wmCoupon]: 'https://p0.meituan.net/pepperfe/a09e2b081c055e18c40a3fc6575a03643198.png',
  // 拼好饭
  [ScpSourceEnum.phf]: 'https://p0.meituan.net/pepperfe/b5935946473b1c31a99f36e5444297a67898.png',
} as Record<ScpSourceEnum, string>;

/**
 * 处理得到新版横版商卡需要的商品信息
 */
export function formatProductInfo(cardInfo: ProductCard, requestId: string, pushCityId?: string, currentTabName?: string): EnhancedProductCard | null {
  try {
    if (!cardInfo) return null;
    const assembleFieldLabel = cardInfo?.tagInfo?.assembleField;
    const skuName =
      cardInfo?.skuInfo?.skuName || cardInfo?.shortTitle || cardInfo?.title;
    const brandName = cardInfo?.poiInfo?.poiBrandName || "";

    return {
      // 商品图片
      image: cardInfo.squareImage || "",
      // 左上角供给标签图片
      supplyTagImage: SUPPLY_TAG_IMAGE_MAP[cardInfo.scpSource] || "",
      // 供给类型
      categoryType:
        PRODUCT_CATEGORY_TYPE_MAP[cardInfo.scpSource] ||
        PRODUCT_CATEGORY_TYPE.GROUP,
      // 店铺所在地理区域
      areaName: cardInfo?.poiInfo?.areaName,
      // 店铺距离
      distance: cardInfo?.poiInfo?.distanceNew,
      // 配送时间
      // deliveryTime: cardInfo?.productTimeInfo?.promisingTime,
      // 商品标题前的标签
      // beforeTitleTag: getBeforeTitleTag(
      //   assembleFieldLabel?.configurableUrlLabel
      // ),
      brandName:
        typeof brandName === "string" && brandName.length > 6
          ? `${brandName.slice(0, 6)}…`
          : brandName,
      skuName,
      productInfoTagList: getProductInfoTagList(
        assembleFieldLabel?.productInfoLabel
      ),
      marketingTagList: getMarketingTagList(assembleFieldLabel),
      availableCouponTag: getAvailableCouponTag(assembleFieldLabel),
      price:
        cardInfo?.priceVO?.afterCashbackPrice ||
        cardInfo?.priceVO?.qqFinalPrice,
      originPrice: cardInfo?.priceVO?.originPrice,
      // wholeSaleNum: cardInfo?.wholeSaleNum,
      priceTag: getPriceTag(assembleFieldLabel?.pricePowerLabel),
      canBuy: cardInfo?.canBuy,
      operation: getProductJumpConfig({
        info: cardInfo,
        globalId: requestId,
        pushCityId,
        channelSource: 'distCenter',
      }),
      poiId: cardInfo?.poiInfo?.poiId || '',
      poiName: cardInfo?.poiInfo?.poiName || '',
      productId: cardInfo?.productId || cardInfo?.skuInfo?.skuId || '',
      secondCategoryId: currentTabName || '',
    };
  } catch (err) {
    reportError({
      name: "[formatProductInfo] formatProductInfoErr",
      msg: err,
      category: "customError",
      level: Level.ERROR,
    });
    return null;
  }
}

// 获取商品信息标签行
export function getProductInfoTagList(productInfoLabelData: ProductInfoLabel[]) {
  if (!Array.isArray(productInfoLabelData) || !productInfoLabelData?.length) {
    return [];
  }
  // 优先级：榜单 > 点评高分 > 销量 > 多店可用
  return productInfoLabelData.slice(0, 2);
}

// 获取营销标签行
export function getMarketingTagList(productAggregationTag: ProductAggregationTag) {
  try {
    const { userTraceLabel, promotionLabel } = productAggregationTag || {};
    if (!Array.isArray(userTraceLabel) && !Array.isArray(promotionLabel)) {
      return [];
    }

    const filterUserTraceLabel = Array.isArray(userTraceLabel)
      ? userTraceLabel.filter((item) => item?.type && item?.labelContent)
      : [];
    const filterPromotionLabel = Array.isArray(promotionLabel)
      ? promotionLabel.filter((item) => item?.type && item?.labelContent &&
       ![PromotionLabelType.AVAILABLE_COUPON, PromotionLabelType.CUT_DOWN].includes(item.type)) // 过滤最大可用券、页面立减标签
      : [];
    const res = [];
    // 用户行为标签，最多展示1个
    if (filterUserTraceLabel?.length) {
      res.push(...filterUserTraceLabel.slice(0, 1));
    }
    // 营销标签，最多展示1个
    if (filterPromotionLabel?.length) {
      res.push(...filterPromotionLabel.slice(0, 1));
    }
    return res;
  } catch (err) {
    reportError({
      name: "[getMarketingTagList] getMarketingTagListErr",
      msg: err,
      category: "supplyError",
      level: Level.ERROR,
    });
    return [];
  }
}

// 获取标题前面的图标
// export function getBeforeTitleTag(configurableUrlLabelData: Label[]) {
//   try {
//     if (!Array.isArray(configurableUrlLabelData)) {
//       return '';
//     }
//     return configurableUrlLabelData[0]?.labelContent || '';
//   } catch (err) {
//     return '';
//   }
// }

// 获取价格力标签
export function getPriceTag(pricePowerLabelData: PricePowerLabel[]): EnhancedPriceLabel | null {
  try {
    if (!Array.isArray(pricePowerLabelData)) {
      return null;
    }
    // 过滤没有类型、文案的标签
    const filterPricePowerLabelData = pricePowerLabelData.filter((label) => label?.type && label?.labelContent);
    if (!filterPricePowerLabelData.length) {
      return null;
    }

    const pricePowerLabel = filterPricePowerLabelData[0];
    return {
      type: pricePowerLabel.type,
      labelContent: pricePowerLabel.labelContent,
      labelDiscount: pricePowerLabel.type === PricePowerLabelType.PERIOD_LOWEST_PRICE
        ? filterPricePowerLabelData.find((label) => [PricePowerLabelType.DISCOUNT, PricePowerLabelType.CUT_PERCENT, PricePowerLabelType.CUT_DOWN].includes(label.type))?.labelContent
        : null,
    };
  } catch (error) {
    reportError({
      name: "[getPriceTag] getPriceTagErr",
      msg: error,
      category: "supplyError",
      level: Level.ERROR,
    });
    return null;
  }
}

export function getAvailableCouponTag(productAggregationTag: ProductAggregationTag): AvailableCouponLabel | null {
  try {
    if (!Array.isArray(productAggregationTag?.promotionLabel) || !productAggregationTag?.promotionLabel?.length) {
      return null;
    }

    const couponLabel = productAggregationTag.promotionLabel.find((item) => [PromotionLabelType.AVAILABLE_COUPON, PromotionLabelType.CUT_DOWN].includes(item?.type));
    if (!couponLabel?.labelItemMap?.amount) return null;

    return {
      prefix: couponLabel?.labelItemMap?.prefix || '',
      amount: couponLabel?.labelItemMap?.amount || '',
      suffix: couponLabel?.labelItemMap?.suffix || '',
      wholeLabel: couponLabel?.labelContent || `${couponLabel?.labelItemMap?.prefix || ''}${couponLabel?.labelItemMap?.amount || ''}${couponLabel?.labelItemMap?.suffix || ''}` || '',
      type: couponLabel.type as PromotionLabelType.AVAILABLE_COUPON | PromotionLabelType.CUT_DOWN,
    };
  } catch (err) {
    reportError({
      name: "[getAvailableCouponTag] getAvailableCouponTagErr",
      msg: err,
      category: "supplyError",
      level: Level.ERROR,
    });
    return null;
  }
}

// 商卡必须包含字段缺失时，上报异常
export function reportProductCardField(productData: EnhancedProductCard) {
  const hasAllField = productData?.image && productData?.skuName &&
    productData?.price && productData?.originPrice && productData?.areaName && productData?.distance;
  if (!hasAllField) {
    reportError({
      name: "[reportProductCardField] productCardFieldMissing",
      category: "supplyError",
      level: Level.ERROR,
      msg: `productId: ${productData?.productId || ''}, productCardFieldMissing: ${JSON.stringify({
        image: !!productData?.image, // 商品图片
        skuName: !!productData?.skuName, // 商品名称
        price: !!productData?.price, // 价格
        originPrice: !!productData?.originPrice, // 原价
        areaName: !!productData?.areaName, // 商圈名称
        distance: !!productData?.distance, // 距离
      })}`,
    });
  }
}
